import { Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { StudentStatusPageShell } from '../../components/dashboard/StudentStatusPageShell';
import { formatDate } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getNeverJoinedDate(item) {
  return toDate(item?.notJoiningAt)
    || toDate(item?.neverJoinedAt)
    || toDate(item?.updatedAt)
    || toDate(item?.createdAt)
    || null;
}

export function NeverJoinedStudentsPage() {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setSignups([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextSignups, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setSignups(nextSignups);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setSignups([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const rows = useMemo(() => {
    const search = normalizeText(searchTerm);

    return signups
      .filter((item) => ['did-not-join', 'never-joined', 'not-joined', 'not_joining'].includes(normalizeText(item.status)))
      .map((item) => ({
        ...item,
        courseName: item.courseName || courseLookup.get(item.courseId)?.name || '-',
        neverJoinedDate: getNeverJoinedDate(item),
        neverJoinedReason: item.notJoiningReason || item.neverJoinedReason || item.note || '',
      }))
      .filter((item) => {
        if (!search) return true;
        return [
          formatStudentName(item),
          item.courseName,
          item.intakeLabel,
          item.neverJoinedReason,
          item.email,
          item.phone,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => {
        const left = a.neverJoinedDate || new Date(0);
        const right = b.neverJoinedDate || new Date(0);
        return right - left;
      });
  }, [courseLookup, searchTerm, signups]);

  const insightCards = [
    { label: 'Did not start', value: rows.length, note: 'Applicants who never began learning.', tone: 'never-joined' },
    { label: 'Courses affected', value: new Set(rows.map((item) => item.courseName).filter(Boolean)).size, note: 'Offers with a missed start.', tone: 'blue' },
    { label: 'Follow-up opportunity', value: signups.length, note: 'The wider enquiry history in this workspace.', tone: 'green' },
  ];

  if (loading) {
    return <LoadingState label="Loading never joined students..." />;
  }

  return (
    <StudentStatusPageShell
      title="Never Joined Students"
      description="See where prospective learners stopped before starting, so your team can follow up, improve the first step, and recover more opportunities."
      kicker="Students"
      stats={[]}
      insightCards={insightCards}
      className="never-joined-page"
      tableTitle="Never Joined"
      tableMeta={`${rows.length} records`}
      tableDescription="Applicants who chose not to join the academy."
      searchId="never-joined-search"
      searchValue={searchTerm}
      onSearchChange={(event) => setSearchTerm(event.target.value)}
      searchPlaceholder="Search student, course, reason..."
      rows={rows}
      columns={[
        {
          key: 'student',
          label: 'Student',
          render: (item) => (
            <div className="appsStudentCell">
              <div className="application-avatar" aria-hidden="true">
                {String(formatStudentName(item)).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ST'}
              </div>
              <div className="application-applicant-copy">
                <strong>{formatStudentName(item)}</strong>
                <span>{item.intakeLabel || 'Open intake'}</span>
              </div>
            </div>
          ),
        },
        { key: 'courseName', label: 'Course', render: (item) => item.courseName || '-' },
        { key: 'intakeLabel', label: 'Cohort', render: (item) => item.intakeLabel || 'Open intake' },
        { key: 'neverJoinedDate', label: 'Marked On', render: (item) => (item.neverJoinedDate ? formatDate(item.neverJoinedDate) : '-') },
        { key: 'neverJoinedReason', label: 'Reason', render: (item) => item.neverJoinedReason || '-' },
        {
          key: 'view',
          label: 'View student',
          render: (item) => (
            <Button to={`/view-student?applicationId=${encodeURIComponent(item.id)}`} variant="secondary" className="appsViewBtn">
              View
            </Button>
          ),
        },
      ]}
      empty={(
        <EmptyState
          title="No never-joined records yet."
          message="Applicants marked as did not join will appear here."
          icon={Users}
        />
      )}
    />
  );
}
